/**
 * Pulse geometry helpers (#489 Phase 2c — the Session Story signature viz).
 *
 * The Pulse is an activity EKG drawn on a session's time axis: a diverging
 * token waveform (output above a centerline, input below), warm pips for
 * genuine human prompts, and flat stretches where the session was idle.
 *
 * The backend emits a per-sitting `pulseBuckets` series (~60 fixed-resolution
 * buckets spanning [startedAt, endedAt]) plus `pulsePrompts` (bucket indices
 * with a genuine user turn). These helpers flatten one or many sittings into
 * the flat bucket/prompt/seam model the <Pulse> component renders, and handle
 * the independent-half normalization the spec calls for (input ≫ output once
 * cache reads dominate, so a shared scale would flatten the output swell).
 */
import type { Sitting, PulseBucket } from '../types/api';

/** One rendered bucket: normalized [0,1] half-heights plus raw magnitudes. */
export interface PulseSample {
  /** Normalized output-token magnitude (drives the swell above the centerline). */
  out: number;
  /** Normalized input-token magnitude (drives the swell below the centerline). */
  in: number;
  /** Raw tool-call count in this bucket (reserved for the tool overlay). */
  tools: number;
  /** True when this bucket carried no activity at all (renders flat). */
  idle: boolean;
}

/** A contiguous run of buckets from one sitting, with the seam that precedes it. */
export interface PulseSegment {
  /** Index into `samples` where this segment starts (inclusive). */
  start: number;
  /** Index into `samples` where this segment ends (exclusive). */
  end: number;
  /** Human label for the seam before this segment (e.g. "14h gap"); null for the first. */
  seamLabel: string | null;
}

export interface PulseData {
  samples: PulseSample[];
  /** Sample indices carrying a genuine human prompt. */
  promptIndices: number[];
  /** Sitting boundaries within `samples`, for drawing resumption seams. */
  segments: PulseSegment[];
}

const bucketOut = (b: PulseBucket): number => parseInt(b.outputTokens || '0', 10);
const bucketIn = (b: PulseBucket): number => parseInt(b.inputTokens || '0', 10);

/**
 * The global height scale for a set of sittings: the ~p95 of active buckets'
 * token totals (input + output), NOT the absolute max. Bars normalize against
 * this and clip to full height (see buildPulse), so a single outlier bucket
 * can't crush the whole waveform to a flat line — the typical bar stays
 * visible and heights read consistently across sessions. The busiest ~5% of
 * buckets simply max out. Pass it into buildPulse for chapter minis so they
 * share the session-wide scale instead of each self-normalizing.
 */
export function pulseScale(sittings: Sitting[]): number {
  const totals: number[] = [];
  for (const s of sittings) {
    for (const b of s.pulseBuckets ?? []) {
      const t = bucketIn(b) + bucketOut(b);
      if (t > 0) totals.push(t);
    }
  }
  if (totals.length === 0) return 0;
  totals.sort((a, b) => a - b);
  const idx = Math.min(totals.length - 1, Math.max(0, Math.ceil(0.95 * totals.length) - 1));
  return totals[idx];
}

const isUnset = (ts: string | undefined): boolean => !ts || parseInt(ts, 10) <= 0;

/** A bucket counts as activity if it has tokens, tool calls, or a human prompt. */
function activeBucket(b: PulseBucket, i: number, promptSet: Set<number>): boolean {
  return bucketOut(b) > 0 || bucketIn(b) > 0 || (b.toolCalls ?? 0) > 0 || promptSet.has(i);
}

/**
 * The wall-clock window of a sitting's actual activity (first→last active
 * bucket), used to (a) trim leading/trailing idle from the drawn series and
 * (b) compute honest seam gaps between sittings. Returns null when the sitting
 * has no activity at all. Buckets divide [startedAt, endedAt] evenly, so a
 * bucket's start time is startedAt + index * bucketDuration.
 */
export function sittingActivityBounds(
  s: Sitting,
): { first: number; last: number; firstMs: number | null; lastMs: number | null } | null {
  const buckets = s.pulseBuckets ?? [];
  if (buckets.length === 0) return null;
  const promptSet = new Set(s.pulsePrompts ?? []);
  let first = -1;
  let last = -1;
  buckets.forEach((b, i) => {
    if (activeBucket(b, i, promptSet)) {
      if (first < 0) first = i;
      last = i;
    }
  });
  if (first < 0) return null;

  const startMs = isUnset(s.startedAt) ? null : parseInt(s.startedAt, 10) * 1000;
  const endMs = isUnset(s.endedAt) ? null : parseInt(s.endedAt, 10) * 1000;
  const bucketMs = startMs != null && endMs != null && endMs > startMs ? (endMs - startMs) / buckets.length : 0;
  return {
    first,
    last,
    firstMs: startMs != null ? startMs + first * bucketMs : null,
    lastMs: startMs != null ? startMs + (last + 1) * bucketMs : null,
  };
}

/** Format a millisecond gap as a compact seam label ("14h", "3d", "45m"). */
export function formatGap(ms: number): string {
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

/**
 * Flatten one or more sittings (in ordinal order) into a single Pulse series.
 * Independent-half normalization is computed across ALL buckets so the whole
 * session shares one vertical scale — a quiet chapter reads as quiet next to a
 * busy one. Sittings with no pulse data (pre-#501, not yet reprocessed) are
 * skipped; the result is empty when nothing has a series yet.
 */
export function buildPulse(sittings: Sitting[], scaleMax?: number): PulseData {
  const ordered = [...sittings].sort((a, b) => a.ordinal - b.ordinal);

  // One global scale: normalize output + input by the ~p95 bucket TOTAL so the
  // two stack honestly (blue output over purple input) and quiet vs heavy
  // stretches are comparable — bars above the scale clip to full height below,
  // so outliers spike rather than flatten everything. scaleMax lets chapter
  // minis share the session scale.
  const max = scaleMax ?? pulseScale(ordered);

  const samples: PulseSample[] = [];
  const promptIndices: number[] = [];
  const segments: PulseSegment[] = [];

  // Track the previous rendered sitting's LAST activity time so a seam's gap
  // spans the real idle between activity — the trimmed trailing idle of the
  // previous sitting + the wall-clock gap + the trimmed leading idle of this
  // one all roll into one honest label (per the A+B plan). Leading/trailing
  // idle is dropped from the drawn series; mid-sitting idle is kept as texture.
  let prevLastMs: number | null = null;
  for (const s of ordered) {
    const buckets = s.pulseBuckets ?? [];
    const bounds = sittingActivityBounds(s);
    if (bounds === null) continue; // no activity to draw; idle rolls into the next seam

    const isFirstSegment = segments.length === 0;

    // Seam label: honest idle gap from the prev sitting's last activity to this
    // sitting's first activity. A context reset (/clear, compaction) is labeled
    // by reason. Sub-minute gaps get a bare seam (no "0m" noise).
    let seamLabel: string | null = null;
    if (!isFirstSegment) {
      if (s.breakReason === 'clear') seamLabel = 'after /clear';
      else if (s.breakReason === 'compaction') seamLabel = 'compact';
      else if (prevLastMs != null && bounds.firstMs != null) {
        const gapMs = bounds.firstMs - prevLastMs;
        if (gapMs >= 60000) seamLabel = formatGap(gapMs);
      }
    }

    const segStart = samples.length;
    const promptSet = new Set(s.pulsePrompts ?? []);
    for (let i = bounds.first; i <= bounds.last; i++) {
      const b = buckets[i];
      const out = bucketOut(b);
      const inp = bucketIn(b);
      const tools = b.toolCalls ?? 0;
      if (promptSet.has(i)) promptIndices.push(samples.length);
      // Clip the stacked height (out+in) to full so outlier buckets above the
      // p95 scale max out instead of overflowing, preserving the out:in split.
      let no = max > 0 ? out / max : 0;
      let ni = max > 0 ? inp / max : 0;
      const total = no + ni;
      if (total > 1) {
        no /= total;
        ni /= total;
      }
      samples.push({
        out: no,
        in: ni,
        tools,
        idle: out === 0 && inp === 0 && tools === 0,
      });
    }

    segments.push({ start: segStart, end: samples.length, seamLabel });
    if (bounds.lastMs != null) prevLastMs = bounds.lastMs;
  }

  return { samples, promptIndices, segments };
}

/** True when there is any Pulse series to draw (i.e. at least one bucket). */
export function hasPulse(data: PulseData): boolean {
  return data.samples.length > 0;
}
